import api from './api';
import { cache } from '../utils/cache';
import type { MachineType, MachineTypeFormData } from '../types/machineType';

interface MachineTypeDTO {
  id: string;
  machine_id: string;
  machineId?: string;
  machine_name?: string;
  machineName?: string;
  type_name?: string;
  name?: string;
  is_active?: boolean;
  isActive?: boolean;
  minimum_input_kg?: number | null;
  maximum_input_kg?: number | null;
  minimumInputKg?: number | null;
  maximumInputKg?: number | null;
  created_at?: string;
  updated_at?: string;
}

const mapDtoToMachineType = (dto: MachineTypeDTO): MachineType => ({
  id: dto.id,
  machineId: dto.machine_id ?? dto.machineId ?? '',
  machineName: dto.machine_name ?? dto.machineName,
  name: dto.type_name ?? dto.name ?? '',
  isActive: dto.is_active ?? dto.isActive ?? true,
  minimumInputKg: dto.minimum_input_kg ?? dto.minimumInputKg ?? undefined,
  maximumInputKg: dto.maximum_input_kg ?? dto.maximumInputKg ?? undefined,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

export const machineTypeService = {
  getAll: async (opts?: { includeOnboardingSamples?: boolean }): Promise<MachineType[]> => {
    const includeSamples = opts?.includeOnboardingSamples === true;
    const fetcher = async () => {
      const params = includeSamples ? { include_onboarding_samples: 'true' } : undefined;
      const response = await api.get<MachineTypeDTO[] | { data: MachineTypeDTO[] }>('/machine-types', { params });
      const data = Array.isArray(response.data)
        ? response.data
        : (Array.isArray((response.data as { data: MachineTypeDTO[] }).data)
            ? (response.data as { data: MachineTypeDTO[] }).data
            : []);
      return data.map(mapDtoToMachineType);
    };
    // Onboarding sample data is volatile (seeded/cleaned mid-tour); never serve the
    // sample-inclusive list from the 5-min cache, or a fetch that ran before the
    // seed would strand the pickers with a stale sample-less list.
    if (includeSamples) return fetcher();
    return cache.fetch('machine_types_all', fetcher);
  },

  getActive: async (opts?: { includeOnboardingSamples?: boolean }): Promise<MachineType[]> => {
    const all = await machineTypeService.getAll(opts);
    return all.filter(t => t.isActive);
  },

  create: async (data: MachineTypeFormData): Promise<MachineType> => {
    const payload = {
      machine_id: data.machineId,
      name: data.name,
      is_active: data.isActive,
      minimum_input_kg: data.minimumInputKg ?? null,
      maximum_input_kg: data.maximumInputKg ?? null,
    };
    const response = await api.post<MachineTypeDTO>('/machine-types', payload);
    cache.invalidate('machine_types');
    cache.invalidate('machine_types_all');
    return mapDtoToMachineType(response.data);
  },

  update: async (id: string, data: Partial<MachineTypeFormData>): Promise<MachineType> => {
    const payload: Record<string, unknown> = {};
    if (data.machineId !== undefined) payload.machine_id = data.machineId;
    if (data.name !== undefined) payload.name = data.name;
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    if (data.minimumInputKg !== undefined) payload.minimum_input_kg = data.minimumInputKg;
    if (data.maximumInputKg !== undefined) payload.maximum_input_kg = data.maximumInputKg;

    const response = await api.put<MachineTypeDTO>(`/machine-types/${id}`, payload);
    cache.invalidate('machine_types');
    cache.invalidate('machine_types_all');
    return mapDtoToMachineType(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/machine-types/${id}`);
    cache.invalidate('machine_types');
    cache.invalidate('machine_types_all');
  },
};
